import { MarketplaceComponentConfig } from '@nocode/types';
import {
  GptConversationResponseModeEnum,
  GptConversationServiceTypeEnum,
} from '@common/constants/gpt-conversation.constant';

const DEFAULT_OPEN_AI_MODEL = 'gpt-4o-mini';
const config: MarketplaceComponentConfig = {
  name: 'GptConversation',
  displayName: {
    en: 'Gpt Conversation',
    ja: 'AIチャットボット',
  },
  icon: './icon.png',
  defaultWidth: 375,
  defaultHeight: 600,
  resizeX: true,
  resizeY: true,
  props: [
    {
      name: 'value',
      displayName: 'OpenAI Thread ID / Responses ID',
      type: 'string',
      role: 'formValue',
    },
    {
      name: 'apiUrl',
      displayName: {
        en: 'API URL',
        ja: 'API URL',
      },
      enabled: {
        serviceType: [GptConversationServiceTypeEnum.dify],
      },
      type: 'text',
      placeholder: 'https://api.dify.ai/v1/chat-messages',
    },
    {
      name: 'serviceType',
      displayName: {
        en: 'Service',
        ja: 'サービス',
      },
      type: 'text',
      default: GptConversationServiceTypeEnum.openAi,
      control: {
        type: 'menu',
        options: [
          {
            label: 'Dify',
            value: GptConversationServiceTypeEnum.dify,
          },
          {
            label: 'OpenAI',
            value: GptConversationServiceTypeEnum.openAi,
          },
          {
            label: 'OpenAI Assistants',
            value: GptConversationServiceTypeEnum.openAiAssistant,
          },
          {
            label: 'OpenAI Responses',
            value: GptConversationServiceTypeEnum.openAiResponses,
          },
        ],
      },
    },
    {
      name: 'responseMode',
      displayName: {
        en: 'Response Mode',
        ja: 'レスポンスモード',
      },
      type: 'text',
      default: GptConversationResponseModeEnum.streaming,
      enabled: {
        serviceType: [GptConversationServiceTypeEnum.dify],
      },
      control: {
        type: 'menu',
        options: [
          {
            label: 'Streaming',
            value: GptConversationResponseModeEnum.streaming,
          },
          {
            label: 'Blocking',
            value: GptConversationResponseModeEnum.blocking,
          },
        ],
      },
    },
    {
      name: 'assistantId',
      displayName: {
        en: 'Assistant ID',
        ja: 'Assistant ID',
      },
      placeholder: 'Assistant ID',
      type: 'text',
      enabled: {
        serviceType: [GptConversationServiceTypeEnum.openAiAssistant],
      },
    },
    {
      name: 'threadId',
      displayName: {
        en: 'Thread ID',
        ja: 'Thread ID',
      },
      placeholder: 'Thread ID',
      type: 'text',
      enabled: {
        serviceType: [GptConversationServiceTypeEnum.openAiAssistant],
      },
    },
    {
      name: 'responseId',
      displayName: {
        en: 'Responses ID',
        ja: 'Responses ID',
      },
      placeholder: 'Responses ID',
      type: 'text',
      enabled: {
        serviceType: [GptConversationServiceTypeEnum.openAiResponses],
      },
    },
    {
      name: 'apiKey',
      displayName: {
        en: 'API Key',
        ja: 'APIキー',
      },
      placeholder: 'xxxx-xxxx-xxxx',
      type: 'text',
      useAppVariable: true,
    },
    {
      name: 'openAiModel',
      displayName: {
        en: 'OpenAI Model',
        ja: 'モデル',
      },
      type: 'text',
      default: DEFAULT_OPEN_AI_MODEL,
      enabled: {
        serviceType: [
          GptConversationServiceTypeEnum.openAi,
          GptConversationServiceTypeEnum.openAiResponses,
        ],
      },
    },
    {
      name: 'initPrompt',
      displayName: {
        en: 'Prompt',
        ja: 'プロンプト',
      },
      type: 'text',
      default: '',
      enabled: {
        serviceType: [
          GptConversationServiceTypeEnum.openAi,
          GptConversationServiceTypeEnum.openAiResponses,
          GptConversationServiceTypeEnum.openAiAssistant,
        ],
      },
    },
    {
      name: 'initMessage',
      displayName: {
        en: 'Initial message',
        ja: '初期メッセージ',
      },
      type: 'text',
      default:
        'こんにちは！私はあなたのお手伝いをするAIチャットボットです。何かお困りのことや質問があれば、ぜひ教えてくださいね！',
    },
    {
      name: 'placeholder',
      displayName: {
        en: 'Input Placeholder',
        ja: '質問のプレースホルダー',
      },
      type: 'text',
      default: 'メッセージ',
      disableDynamicValues: true,
    },
    {
      name: 'supportUploadFile',
      displayName: {
        en: 'Support Upload File',
        ja: 'ファイル添付',
      },
      type: 'boolean',
      default: false,
      enabled: {
        serviceType: [
          GptConversationServiceTypeEnum.openAiAssistant,
          GptConversationServiceTypeEnum.openAiResponses,
        ],
      },
    },
    {
      name: 'webSearchTool',
      displayName: {
        en: 'Web Search Tool',
        ja: 'Web検索ツール',
      },
      type: 'boolean',
      default: false,
      enabled: {
        serviceType: [GptConversationServiceTypeEnum.openAiResponses],
      },
    },
    {
      name: 'backgroundColor',
      displayName: {
        en: 'Background Color',
        ja: '背景色',
      },
      type: 'color',
      default: '#34A0DD',
    },
    {
      name: 'fontSize',
      type: 'fontSize',
    },
    {
      name: 'fontFamily',
      type: 'fontFamily',
    },
  ],
  childComponents: [
    {
      name: 'chatbot',
      displayName: {
        en: 'AI Chatbot',
        ja: 'AIチャットボット名',
      },
      props: [
        {
          name: 'name',
          displayName: {
            en: 'AI Name',
            ja: 'AIの名前',
          },
          type: 'text',
          default: 'Jack',
        },
        {
          name: 'color',
          type: 'color',
          displayName: {
            en: 'Text color',
            ja: 'AI側のテキスト色',
          },
          default: '#8876ff',
        },
        {
          name: 'backgroundColor',
          displayName: {
            en: 'Background Color',
            ja: '背景色',
          },
          type: 'color',
          default: '#34A0DD',
        },
        {
          name: 'icon',
          displayName: {
            en: 'AI Chatbot Icon',
            ja: 'AIのアイコン',
          },
          type: 'icon',
          default: 'AIチャットボット名',
        },
        {
          name: 'iconColor',
          type: 'color',
          displayName: {
            en: 'Icon color',
            ja: 'アイコン色',
          },
          default: '#8876ff',
        },
        {
          name: 'iconBackgroundColor',
          displayName: {
            en: 'Icon Background Color',
            ja: 'アイコンの背景色',
          },
          type: 'color',
          default: '#34A0DD',
          styles: {
            backgroundColor: true,
          },
        },
      ],
      styles: {
        backgroundColor: true,
        color: true,
        iconColor: true,
      },
    },
    {
      name: 'user',
      displayName: {
        en: 'User',
        ja: 'ユーザー',
      },
      props: [
        {
          name: 'name',
          displayName: {
            en: 'Username',
            ja: 'ユーザーの名前',
          },
          type: 'text',
          default: 'Jack',
        },
        {
          name: 'color',
          type: 'color',
          displayName: {
            en: 'Text color',
            ja: 'テキスト色',
          },
          default: '#8876ff',
        },
        {
          name: 'backgroundColor',
          displayName: {
            en: 'Background Color',
            ja: '背景色',
          },
          type: 'color',
          default: '#34A0DD',
        },
        {
          name: 'icon',
          displayName: {
            en: 'User Icon',
            ja: 'ユーザーのアイコン',
          },
          type: 'icon',
          default: 'AIチャットボット名',
        },
        {
          name: 'iconColor',
          type: 'color',
          displayName: {
            en: 'Icon color',
            ja: 'アイコン色',
          },
          default: '#8876ff',
        },
        {
          name: 'iconBackgroundColor',
          displayName: {
            en: 'Icon Background Color',
            ja: 'アイコンの背景色',
          },
          type: 'color',
          default: '#34A0DD',
          styles: {
            backgroundColor: true,
          },
        },
      ],
      styles: {
        backgroundColor: true,
        color: true,
        iconColor: true,
      },
    },
    {
      name: 'sendButton',
      displayName: {
        en: 'Submit Button',
        ja: '送信ボタン',
      },
      props: [
        {
          name: 'text',
          displayName: {
            en: 'Text',
            ja: 'テキスト',
          },
          type: 'text',
          default: '支払い',
        },
        {
          name: 'icon',
          displayName: {
            en: 'Icon',
            ja: 'アイコン',
          },
          type: 'icon',
          default: 'add',
        },
        {
          name: 'color',
          type: 'color',
          displayName: {
            en: 'Text color',
            ja: 'ボタンのテキスト色',
          },
          default: '#000',
        },
        {
          name: 'backgroundColor',
          displayName: {
            en: 'Background Color',
            ja: 'ボタンの色',
          },
          type: 'color',
          default: '#fff',
        },
      ],
      styles: {
        backgroundColor: true,
        color: true,
      },
    },
  ],
  style: {
    text: true,
    backgroundColor: true,
    borderRadius: true,
  },
};

export default config;
